// profileService.js
// Wrappers + cache + React hook for GET/PUT /user-profile.
//
// PRD-64: This is the single source of truth for `onboardingState` and the
// related milestone fields (firstAssetAt, firstScanCompletedAt, etc.) in the
// frontend. Multiple components (OnboardingWizard, QuickStartGuide,
// SetupChecklist, CommandCenter, Settings) need the same profile data —
// a module-level promise cache ensures ONE GET serves all consumers per
// mount cycle, with explicit invalidation after writes.

import { useEffect, useState, useCallback } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL
    || 'https://driftalarm-functions-bbfmfuh5hmdsfcc7.eastus-01.azurewebsites.net/api';

async function getAuthToken() {
    const msalInstance = window.msalInstance;
    if (!msalInstance) return null;
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) return null;
    try {
        const response = await msalInstance.acquireTokenSilent({
            scopes: ['openid', 'profile', 'email'],
            account: accounts[0],
        });
        return response.idToken;
    } catch {
        return null;
    }
}

// Module-level promise cache so multiple components share one in-flight fetch.
let _profilePromise = null;

function fetchProfile() {
    return (async () => {
        const token = await getAuthToken();
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch(`${API_BASE_URL}/user-profile`, { headers });
        if (!res.ok) throw new Error(`Failed to fetch profile: ${res.status}`);
        const data = await res.json();
        return data.profile || null;
    })();
}

export async function getProfile({ forceRefresh = false } = {}) {
    if (forceRefresh || !_profilePromise) {
        _profilePromise = fetchProfile();
    }
    try {
        return await _profilePromise;
    } catch (err) {
        _profilePromise = null;
        throw err;
    }
}

export function invalidateProfileCache() {
    _profilePromise = null;
}

// PUT /user-profile with a partial body. Invalidates the cache so the next
// read returns the freshly-merged server state.
export async function updateProfile(patch) {
    const token = await getAuthToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${API_BASE_URL}/user-profile`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(patch),
    });
    invalidateProfileCache();
    if (!res.ok) {
        let detail = '';
        try { detail = (await res.json())?.error || ''; } catch {}
        throw new Error(`Failed to update profile: ${res.status}${detail ? ' — ' + detail : ''}`);
    }
    return await res.json();
}

// Partial-update shortcut for the onboardingState sub-object.
export async function updateOnboardingState(patch) {
    return updateProfile({ onboardingState: patch });
}

// React hook for components that need profile + onboardingState.
// Returns { profile, onboardingState, loading, error, refresh }.
// While `loading` is true, callers MUST NOT render gated UI (wizard, QSG,
// checklist) — see PRD-64 §3.4 loading-guard requirement.
export function useOnboardingState() {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async ({ forceRefresh = false } = {}) => {
        setLoading(true);
        setError(null);
        try {
            const p = await getProfile({ forceRefresh });
            setProfile(p);
        } catch (err) {
            setError(err);
            setProfile(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const p = await getProfile();
                if (!cancelled) setProfile(p);
            } catch (err) {
                if (!cancelled) {
                    setError(err);
                    setProfile(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const refresh = useCallback(() => load({ forceRefresh: true }), [load]);

    return {
        profile,
        onboardingState: profile?.onboardingState || null,
        loading,
        error,
        refresh,
    };
}
